import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconBriefcase,
    IconFileTypePdf,
    IconPlus,
    IconPrinter,
    IconX,
} from '@tabler/icons-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import AtsCvPreviewFrame from './components/AtsCvPreviewFrame';
import AtsCvFormWizardNav from './components/AtsCvFormWizardNav';
import AtsCvFormFooter from './components/AtsCvFormFooter';
import AtsCvPdfImport from './components/AtsCvPdfImport';
import AtsCvSectionTitleInput from './components/AtsCvSectionTitleInput';
import AtsCvTemplatePicker from './components/AtsCvTemplatePicker';
import AtsCvPhotoField from './components/AtsCvPhotoField';
import {
    INITIAL_ATS_CV_FORM,
    createExperienceEntry,
    createEducationEntry,
    createProjectEntry,
    createCustomSection,
    displayField,
    saveAtsCvFormSnapshot,
    saveAtsCvPersonalHistory,
} from './engines/atsCvFormEngine';
import { ATS_CV_WIZARD_LAST_INDEX } from './engines/atsCvWizardSteps';
import { downloadAtsCvPdf } from './engines/generateAtsCvPdf';
import { printAtsCvPreview } from './engines/printAtsCvPreview';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-orange-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-orange-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

const CUSTOM_LAYOUT_VALUES = ['bullets', 'twoColumn', 'paragraph'];

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children, error }) {
    return (
        <div
            className={`rounded-2xl border bg-white px-4 py-4 dark:bg-zinc-900 ${
                error
                    ? 'border-red-400 dark:border-red-600'
                    : 'border-orange-200 dark:border-orange-900/40'
            }`}
        >
            <h3 className="mb-3.5 text-[13.5px] font-bold text-orange-700 dark:text-orange-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

function TextInput({
    id,
    name,
    value,
    onChange,
    placeholder,
    error,
    type = 'text',
    autoComplete,
    disabled = false,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
                disabled={disabled}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass} disabled:cursor-not-allowed disabled:opacity-60`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

function TextArea({
    id,
    value,
    onChange,
    placeholder,
    label,
    rows = 3,
}) {
    return (
        <div>
            {label ? <FormLabel htmlFor={id}>{label}</FormLabel> : null}
            <textarea
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className={`${inputBaseClass} ${inputOkClass} min-h-[80px] resize-y`}
            />
        </div>
    );
}

export default function AtsCvMakerGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);

    const [form, setForm] = useState(INITIAL_ATS_CV_FORM);
    const [template, setTemplate] = useState('classic');
    const pageZoom = usePageZoom();
    const [wizardStep, setWizardStep] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');

    const update = useCallback((key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const updateExperience = useCallback((id, key, value) => {
        setForm((prev) => ({
            ...prev,
            experiences: prev.experiences.map((e) => {
                if (e.id !== id) return e;
                const next = { ...e, [key]: value };
                if (key === 'isCurrent' && value) next.endDate = '';
                return next;
            }),
        }));
    }, []);

    const updateEducation = useCallback((id, key, value) => {
        setForm((prev) => ({
            ...prev,
            education: prev.education.map((e) => (e.id === id ? { ...e, [key]: value } : e)),
        }));
    }, []);

    const updateProject = useCallback((id, key, value) => {
        setForm((prev) => ({
            ...prev,
            projects: prev.projects.map((p) => (p.id === id ? { ...p, [key]: value } : p)),
        }));
    }, []);

    const updateSectionTitle = useCallback((key, value) => {
        setForm((prev) => ({
            ...prev,
            sectionTitles: { ...prev.sectionTitles, [key]: value },
        }));
    }, []);

    const updateCustomSection = useCallback((id, key, value) => {
        setForm((prev) => ({
            ...prev,
            customSections: prev.customSections.map((s) =>
                (s.id === id ? { ...s, [key]: value } : s)),
        }));
    }, []);

    const addExperience = () => {
        setForm((prev) => ({
            ...prev,
            experiences: [...prev.experiences, createExperienceEntry()],
        }));
    };

    const removeExperience = (id) => {
        setForm((prev) => ({
            ...prev,
            experiences:
                prev.experiences.length > 1
                    ? prev.experiences.filter((e) => e.id !== id)
                    : prev.experiences,
        }));
    };

    const addEducation = () => {
        setForm((prev) => ({
            ...prev,
            education: [...prev.education, createEducationEntry()],
        }));
    };

    const removeEducation = (id) => {
        setForm((prev) => ({
            ...prev,
            education:
                prev.education.length > 1
                    ? prev.education.filter((e) => e.id !== id)
                    : prev.education,
        }));
    };

    const addProject = () => {
        setForm((prev) => ({
            ...prev,
            projects: [...prev.projects, createProjectEntry()],
        }));
    };

    const removeProject = (id) => {
        setForm((prev) => ({
            ...prev,
            projects: prev.projects.filter((p) => p.id !== id),
        }));
    };

    const addCustomSection = () => {
        setForm((prev) => ({
            ...prev,
            customSections: [...prev.customSections, createCustomSection()],
        }));
    };

    const removeCustomSection = (id) => {
        setForm((prev) => ({
            ...prev,
            customSections: prev.customSections.filter((s) => s.id !== id),
        }));
    };

    const handleClear = () => {
        setForm(INITIAL_ATS_CV_FORM);
        setWizardStep(0);
        notifications.show({
            title: t('atsCvMaker.clearedTitle'),
            message: t('atsCvMaker.clearedMessage'),
            color: 'gray',
        });
    };

    const handlePdfImport = (importedForm) => {
        setForm(importedForm);
        setWizardStep(1);
        notifications.show({
            title: t('atsCvMaker.import.reviewTitle'),
            message: t('atsCvMaker.import.reviewMessage'),
            color: 'green',
            autoClose: 5000,
        });
    };

    // Every field is optional: the wizard is a guide, not a gate. Steps, Next,
    // Print and Download all work on whatever has been typed so far.
    const goWizardNext = () => {
        if (wizardStep < ATS_CV_WIZARD_LAST_INDEX) {
            setWizardStep(wizardStep + 1);
            return;
        }

        previewRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        notifications.show({
            title: t('atsCvMaker.formCompleteTitle'),
            message: t('atsCvMaker.formCompleteMessage'),
            color: 'green',
            autoClose: 6000,
        });
    };

    const goWizardPrev = () => {
        if (wizardStep > 0) setWizardStep(wizardStep - 1);
    };

    const getDefaultDownloadFileName = () => {
        const base =
            form.fullName
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\u0980-\u09FF-]+/gi, '')
                .replace(/-+/g, '-')
                .slice(0, 40) || 'cv';
        return `ats-cv-${base}`;
    };

    const openDownloadModal = () => {
        setDownloadFileName(getDefaultDownloadFileName());
        setDownloadModalOpen(true);
    };

    const handleConfirmDownloadPdf = async () => {
        const raw = downloadFileName.trim();
        if (!raw) return;

        const filename = raw.toLowerCase().endsWith('.pdf') ? raw : `${raw}.pdf`;

        setDownloading(true);
        try {
            recordToolUsage('general/ats-cv-maker', { category: 'general' });
            await downloadAtsCvPdf(previewRef.current, form, filename);
            saveAtsCvPersonalHistory(form);
            saveAtsCvFormSnapshot(form);
            setDownloadModalOpen(false);
            notifications.show({
                title: t('common.success'),
                message: t('atsCvMaker.downloadSuccess', { filename }),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('atsCvMaker.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = async () => {
        recordToolUsage('general/ats-cv-maker', { category: 'general' });
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('atsCvMaker.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printAtsCvPreview(previewRef.current);
            notifications.show({
                title: t('atsCvMaker.printTitle'),
                message: t('atsCvMaker.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('atsCvMaker.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const hasAnyInput = useMemo(() => {
        const scalarFields = [
            'fullName', 'jobTitle', 'email', 'phone', 'location', 'linkedin', 'website',
            'summary', 'skills', 'certifications', 'languages',
        ];
        if (scalarFields.some((key) => displayField(form[key]))) return true;
        if ((form.experiences || []).some((e) =>
            displayField(e.jobTitle) || displayField(e.company) || displayField(e.description))) {
            return true;
        }
        if ((form.education || []).some((e) =>
            displayField(e.degree) || displayField(e.institution))) {
            return true;
        }
        if ((form.projects || []).some((p) => displayField(p.name))) return true;
        if ((form.customSections || []).some((s) =>
            displayField(s.title) || displayField(s.content))) {
            return true;
        }
        return false;
    }, [form]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: hasAnyInput,
        hasResult: hasAnyInput,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('atsCvMaker.statusReady'),
        success: t('atsCvMaker.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`atsCvMaker.howTo.step${i}`)),
        [t],
    );


    const addButtonClass = 'inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-[12.5px] font-semibold text-orange-700 transition-colors hover:bg-orange-100 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-orange-300 dark:hover:bg-orange-950/40';

    return (
        <>
            <ToolPageLayout
                slug="general/ats-cv-maker"
                subtitleKey="atsCvMaker.subtitle"
                // 30/70, the family-card form's split: the CV is what anyone is
                // reading as they type, and an A4 page needs the room to stay
                // legible, so the fields take the narrow column and the preview
                // becomes the sticky rail beside them. The 9px is half the gap.
                variant="wideControls"
                railWidthClass="lg:w-[calc(70%_-_9px)]"
                status={status}
                statusText={statusText}
                controlsTitle={t('atsCvMaker.settingsTitle')}
                onReset={hasAnyInput ? handleClear : undefined}
                resetLabel={t('atsCvMaker.footer.clearAll')}
                primaryAction={{
                    label: downloading ? t('atsCvMaker.footer.downloading') : t('atsCvMaker.footer.download'),
                    onClick: openDownloadModal,
                    disabled: printing || downloadModalOpen,
                    loading: downloading,
                    icon: IconFileTypePdf,
                }}
                // Print rides in the sticky bar with Download and Clear, so
                // every action on the CV is in one place at the foot of the
                // screen rather than parked beside a column already scrolled past.
                secondaryAction={[
                    {
                        key: 'print',
                        label: printing ? t('atsCvMaker.footer.printing') : t('atsCvMaker.footer.print'),
                        onClick: handlePrint,
                        disabled: downloading || printing || downloadModalOpen,
                        icon: IconPrinter,
                    },
                ]}
                howToSteps={howToSteps}
                controls={(
                    <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                        <p className="text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                            {t('atsCvMaker.settingsHint')}
                        </p>

                        <AtsCvTemplatePicker selected={template} onSelect={setTemplate} form={form} />

                        <AtsCvFormWizardNav
                            activeStep={wizardStep}
                            form={form}
                            onStepChange={setWizardStep}
                            onNext={goWizardNext}
                        />

                        {wizardStep === 0 && (
                            <FormSection
                                number={t('atsCvMaker.sections.import.number')}
                                title={t('atsCvMaker.sections.import.title')}
                            >
                                <p className="mb-3 text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                                    {t('atsCvMaker.sections.import.hint')}
                                </p>
                                <AtsCvPdfImport onImported={handlePdfImport} />
                            </FormSection>
                        )}

                        {wizardStep === 1 && (
                            <FormSection
                                number={t('atsCvMaker.sections.personal.number')}
                                title={t('atsCvMaker.sections.personal.title')}
                            >
                                <div className="space-y-3">
                                    <div>
                                        <FormLabel>{t('atsCvMaker.fields.photo')}</FormLabel>
                                        <AtsCvPhotoField
                                            value={form.photo}
                                            source={form.photoSource}
                                            crop={form.photoCrop}
                                            // Three fields move together — the
                                            // picture, what it came from, and
                                            // the framing between them.
                                            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="fullName">{t('atsCvMaker.fields.fullName')}</FormLabel>
                                        <TextInput
                                            id="fullName"
                                            name="fullName"
                                            value={form.fullName}
                                            onChange={(v) => update('fullName', v)}
                                            placeholder={t('atsCvMaker.placeholders.fullName')}
                                            autoComplete="name"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="jobTitle">{t('atsCvMaker.fields.jobTitle')}</FormLabel>
                                        <TextInput
                                            id="jobTitle"
                                            name="jobTitle"
                                            value={form.jobTitle}
                                            onChange={(v) => update('jobTitle', v)}
                                            placeholder={t('atsCvMaker.placeholders.jobTitle')}
                                            autoComplete="organization-title"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="email">{t('atsCvMaker.fields.email')}</FormLabel>
                                        <TextInput
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={(v) => update('email', v)}
                                            placeholder={t('atsCvMaker.placeholders.email')}
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="phone">{t('atsCvMaker.fields.phone')}</FormLabel>
                                        <TextInput
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={(v) => update('phone', v)}
                                            placeholder={t('atsCvMaker.placeholders.phone')}
                                            autoComplete="tel"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="location">{t('atsCvMaker.fields.location')}</FormLabel>
                                        <TextInput
                                            id="location"
                                            name="location"
                                            value={form.location}
                                            onChange={(v) => update('location', v)}
                                            placeholder={t('atsCvMaker.placeholders.location')}
                                            autoComplete="street-address"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="linkedin">{t('atsCvMaker.fields.linkedin')}</FormLabel>
                                        <TextInput
                                            id="linkedin"
                                            name="linkedin"
                                            value={form.linkedin}
                                            onChange={(v) => update('linkedin', v)}
                                            placeholder={t('atsCvMaker.placeholders.linkedin')}
                                            autoComplete="url"
                                        />
                                    </div>
                                    <div>
                                        <FormLabel htmlFor="website">{t('atsCvMaker.fields.website')}</FormLabel>
                                        <TextInput
                                            id="website"
                                            name="website"
                                            value={form.website}
                                            onChange={(v) => update('website', v)}
                                            placeholder={t('atsCvMaker.placeholders.website')}
                                            autoComplete="url"
                                        />
                                    </div>
                                </div>
                            </FormSection>
                        )}

                        {wizardStep === 2 && (
                            <FormSection
                                number={t('atsCvMaker.sections.summary.number')}
                                title={t('atsCvMaker.sections.summary.title')}
                            >
                                <AtsCvSectionTitleInput
                                    value={form.sectionTitles.summary}
                                    onChange={(v) => updateSectionTitle('summary', v)}
                                />
                                <TextArea
                                    id="summary"
                                    value={form.summary}
                                    onChange={(v) => update('summary', v)}
                                    placeholder={t('atsCvMaker.placeholders.summary')}
                                />
                            </FormSection>
                        )}

                        {wizardStep === 3 && (
                            <FormSection
                                number={t('atsCvMaker.sections.experience.number')}
                                title={t('atsCvMaker.sections.experience.title')}
                            >
                                <AtsCvSectionTitleInput
                                    value={form.sectionTitles.experience}
                                    onChange={(v) => updateSectionTitle('experience', v)}
                                />
                                <div className="space-y-4">
                                    {form.experiences.map((exp, idx) => (
                                        <div key={exp.id}>
                                            {idx > 0 ? (
                                                <hr className="mb-3 border-[#ECE8E4] dark:border-zinc-700" />
                                            ) : null}
                                            <div className="mb-2 flex items-center justify-between">
                                                <p className="text-[11px] font-semibold text-[#A8A099] dark:text-zinc-500">
                                                    {t('atsCvMaker.labels.experienceEntry', { index: idx + 1 })}
                                                </p>
                                                {form.experiences.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeExperience(exp.id)}
                                                        className="rounded-lg p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
                                                        aria-label={t('atsCvMaker.actions.remove')}
                                                    >
                                                        <IconX size={14} />
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.expJobTitle')}</FormLabel>
                                                    <TextInput
                                                        value={exp.jobTitle}
                                                        onChange={(v) => updateExperience(exp.id, 'jobTitle', v)}
                                                        placeholder={t('atsCvMaker.placeholders.expJobTitle')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.company')}</FormLabel>
                                                    <TextInput
                                                        value={exp.company}
                                                        onChange={(v) => updateExperience(exp.id, 'company', v)}
                                                        placeholder={t('atsCvMaker.placeholders.company')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.location')}</FormLabel>
                                                    <TextInput
                                                        value={exp.location}
                                                        onChange={(v) => updateExperience(exp.id, 'location', v)}
                                                        placeholder={t('atsCvMaker.placeholders.expLocation')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.startDate')}</FormLabel>
                                                    <TextInput
                                                        value={exp.startDate}
                                                        onChange={(v) => updateExperience(exp.id, 'startDate', v)}
                                                        placeholder={t('atsCvMaker.placeholders.startDate')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.endDate')}</FormLabel>
                                                    <TextInput
                                                        value={exp.endDate}
                                                        onChange={(v) => updateExperience(exp.id, 'endDate', v)}
                                                        placeholder={t('atsCvMaker.placeholders.endDate')}
                                                        disabled={exp.isCurrent}
                                                    />
                                                </div>
                                                <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#3D3833] dark:text-zinc-200">
                                                    <input
                                                        type="checkbox"
                                                        checked={exp.isCurrent}
                                                        onChange={(e) =>
                                                            updateExperience(exp.id, 'isCurrent', e.target.checked)}
                                                        className="h-4 w-4 rounded border-[#ECE8E4] text-orange-600 focus:ring-orange-400/30 dark:border-zinc-600"
                                                    />
                                                    {t('atsCvMaker.fields.isCurrent')}
                                                </label>
                                                <TextArea
                                                    label={t('atsCvMaker.fields.expDescription')}
                                                    value={exp.description}
                                                    onChange={(v) => updateExperience(exp.id, 'description', v)}
                                                    placeholder={t('atsCvMaker.placeholders.expDescription')}
                                                    rows={4}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addExperience} className={addButtonClass}>
                                        <IconPlus size={12} />
                                        {t('atsCvMaker.actions.addExperience')}
                                    </button>
                                </div>
                            </FormSection>
                        )}

                        {wizardStep === 4 && (
                            <FormSection
                                number={t('atsCvMaker.sections.education.number')}
                                title={t('atsCvMaker.sections.education.title')}
                            >
                                <AtsCvSectionTitleInput
                                    value={form.sectionTitles.education}
                                    onChange={(v) => updateSectionTitle('education', v)}
                                />
                                <div className="space-y-4">
                                    {form.education.map((edu, idx) => (
                                        <div key={edu.id}>
                                            {idx > 0 ? (
                                                <hr className="mb-3 border-[#ECE8E4] dark:border-zinc-700" />
                                            ) : null}
                                            <div className="mb-2 flex items-center justify-between">
                                                <p className="text-[11px] font-semibold text-[#A8A099] dark:text-zinc-500">
                                                    {t('atsCvMaker.labels.educationEntry', { index: idx + 1 })}
                                                </p>
                                                {form.education.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeEducation(edu.id)}
                                                        className="rounded-lg p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
                                                        aria-label={t('atsCvMaker.actions.remove')}
                                                    >
                                                        <IconX size={14} />
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.degree')}</FormLabel>
                                                    <TextInput
                                                        value={edu.degree}
                                                        onChange={(v) => updateEducation(edu.id, 'degree', v)}
                                                        placeholder={t('atsCvMaker.placeholders.degree')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.institution')}</FormLabel>
                                                    <TextInput
                                                        value={edu.institution}
                                                        onChange={(v) => updateEducation(edu.id, 'institution', v)}
                                                        placeholder={t('atsCvMaker.placeholders.institution')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.location')}</FormLabel>
                                                    <TextInput
                                                        value={edu.location}
                                                        onChange={(v) => updateEducation(edu.id, 'location', v)}
                                                        placeholder={t('atsCvMaker.placeholders.eduLocation')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.graduationYear')}</FormLabel>
                                                    <TextInput
                                                        value={edu.graduationYear}
                                                        onChange={(v) => updateEducation(edu.id, 'graduationYear', v)}
                                                        placeholder={t('atsCvMaker.placeholders.graduationYear')}
                                                    />
                                                </div>
                                                <TextArea
                                                    label={t('atsCvMaker.fields.eduDetails')}
                                                    value={edu.details}
                                                    onChange={(v) => updateEducation(edu.id, 'details', v)}
                                                    placeholder={t('atsCvMaker.placeholders.eduDetails')}
                                                    rows={3}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addEducation} className={addButtonClass}>
                                        <IconPlus size={12} />
                                        {t('atsCvMaker.actions.addEducation')}
                                    </button>
                                </div>
                            </FormSection>
                        )}

                        {wizardStep === 5 && (
                            <FormSection
                                number={t('atsCvMaker.sections.projects.number')}
                                title={t('atsCvMaker.sections.projects.title')}
                            >
                                <AtsCvSectionTitleInput
                                    value={form.sectionTitles.projects}
                                    onChange={(v) => updateSectionTitle('projects', v)}
                                />
                                <div className="space-y-4">
                                    {form.projects.length === 0 ? (
                                        <p className="text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                                            {t('atsCvMaker.sections.projects.emptyHint')}
                                        </p>
                                    ) : null}
                                    {form.projects.map((proj, idx) => (
                                        <div key={proj.id}>
                                            {idx > 0 ? (
                                                <hr className="mb-3 border-[#ECE8E4] dark:border-zinc-700" />
                                            ) : null}
                                            <div className="mb-2 flex items-center justify-between">
                                                <p className="text-[11px] font-semibold text-[#A8A099] dark:text-zinc-500">
                                                    {t('atsCvMaker.labels.projectEntry', { index: idx + 1 })}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => removeProject(proj.id)}
                                                    className="rounded-lg p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
                                                    aria-label={t('atsCvMaker.actions.remove')}
                                                >
                                                    <IconX size={14} />
                                                </button>
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.projectName')}</FormLabel>
                                                    <TextInput
                                                        value={proj.name}
                                                        onChange={(v) => updateProject(proj.id, 'name', v)}
                                                        placeholder={t('atsCvMaker.placeholders.projectName')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.projectLink')}</FormLabel>
                                                    <TextInput
                                                        value={proj.link}
                                                        onChange={(v) => updateProject(proj.id, 'link', v)}
                                                        placeholder={t('atsCvMaker.placeholders.projectLink')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.projectDate')}</FormLabel>
                                                    <TextInput
                                                        value={proj.date}
                                                        onChange={(v) => updateProject(proj.id, 'date', v)}
                                                        placeholder={t('atsCvMaker.placeholders.projectDate')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.technologies')}</FormLabel>
                                                    <TextInput
                                                        value={proj.technologies}
                                                        onChange={(v) => updateProject(proj.id, 'technologies', v)}
                                                        placeholder={t('atsCvMaker.placeholders.technologies')}
                                                    />
                                                </div>
                                                <TextArea
                                                    label={t('atsCvMaker.fields.projectDescription')}
                                                    value={proj.description}
                                                    onChange={(v) => updateProject(proj.id, 'description', v)}
                                                    placeholder={t('atsCvMaker.placeholders.projectDescription')}
                                                    rows={4}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addProject} className={addButtonClass}>
                                        <IconPlus size={12} />
                                        {t('atsCvMaker.actions.addProject')}
                                    </button>
                                </div>
                            </FormSection>
                        )}

                        {wizardStep === 6 && (
                            <FormSection
                                number={t('atsCvMaker.sections.skills.number')}
                                title={t('atsCvMaker.sections.skills.title')}
                            >
                                <div className="space-y-4">
                                    <div>
                                        <AtsCvSectionTitleInput
                                            value={form.sectionTitles.skills}
                                            onChange={(v) => updateSectionTitle('skills', v)}
                                        />
                                        <TextArea
                                            label={t('atsCvMaker.fields.skills')}
                                            value={form.skills}
                                            onChange={(v) => update('skills', v)}
                                            placeholder={t('atsCvMaker.placeholders.skills')}
                                            rows={4}
                                        />
                                    </div>
                                    <div>
                                        <AtsCvSectionTitleInput
                                            value={form.sectionTitles.certifications}
                                            onChange={(v) => updateSectionTitle('certifications', v)}
                                        />
                                        <TextArea
                                            label={t('atsCvMaker.fields.certifications')}
                                            value={form.certifications}
                                            onChange={(v) => update('certifications', v)}
                                            placeholder={t('atsCvMaker.placeholders.certifications')}
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <AtsCvSectionTitleInput
                                            value={form.sectionTitles.languages}
                                            onChange={(v) => updateSectionTitle('languages', v)}
                                        />
                                        <TextArea
                                            label={t('atsCvMaker.fields.languages')}
                                            value={form.languages}
                                            onChange={(v) => update('languages', v)}
                                            placeholder={t('atsCvMaker.placeholders.languages')}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </FormSection>
                        )}

                        {wizardStep === 7 && (
                            <FormSection
                                number={t('atsCvMaker.sections.custom.number')}
                                title={t('atsCvMaker.sections.custom.title')}
                            >
                                <p className="mb-3 text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                                    {t('atsCvMaker.sections.custom.hint')}
                                </p>
                                <div className="space-y-4">
                                    {form.customSections.map((sec, idx) => (
                                        <div key={sec.id}>
                                            {idx > 0 ? (
                                                <hr className="mb-3 border-[#ECE8E4] dark:border-zinc-700" />
                                            ) : null}
                                            <div className="mb-2 flex items-center justify-between">
                                                <p className="text-[11px] font-semibold text-[#A8A099] dark:text-zinc-500">
                                                    {t('atsCvMaker.labels.customEntry', { index: idx + 1 })}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => removeCustomSection(sec.id)}
                                                    className="rounded-lg p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
                                                    aria-label={t('atsCvMaker.actions.remove')}
                                                >
                                                    <IconX size={14} />
                                                </button>
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.customTitle')}</FormLabel>
                                                    <TextInput
                                                        value={sec.title}
                                                        onChange={(v) => updateCustomSection(sec.id, 'title', v)}
                                                        placeholder={t('atsCvMaker.placeholders.customTitle')}
                                                    />
                                                </div>
                                                <div>
                                                    <FormLabel>{t('atsCvMaker.fields.customLayout')}</FormLabel>
                                                    <Select
                                                        value={sec.layout}
                                                        onValueChange={(v) =>
                                                            updateCustomSection(sec.id, 'layout', v || 'bullets')}
                                                    >
                                                        <SelectTrigger className="h-[42px] rounded-xl">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {CUSTOM_LAYOUT_VALUES.map((value) => (
                                                                <SelectItem key={value} value={value}>
                                                                    {t(`atsCvMaker.layouts.${value}`)}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <TextArea
                                                    label={
                                                        sec.layout === 'paragraph'
                                                            ? t('atsCvMaker.fields.customParagraph')
                                                            : t('atsCvMaker.fields.customContent')
                                                    }
                                                    value={sec.content}
                                                    onChange={(v) => updateCustomSection(sec.id, 'content', v)}
                                                    placeholder={
                                                        sec.layout === 'paragraph'
                                                            ? t('atsCvMaker.placeholders.customParagraph')
                                                            : t('atsCvMaker.placeholders.customContent')
                                                    }
                                                    rows={4}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addCustomSection} className={addButtonClass}>
                                        <IconPlus size={12} />
                                        {t('atsCvMaker.actions.addCustomSection')}
                                    </button>
                                </div>
                            </FormSection>
                        )}
                    </div>
                )}
                settings={(
                    <AtsCvFormFooter
                        activeStep={wizardStep}
                        onNext={goWizardNext}
                        onPrev={goWizardPrev}
                    />
                )}
                preview={(
                    // A flex column, not a plain block: the preview panel
                    // stretches itself, and inside a block wrapper that height
                    // would resolve against nothing.
                    <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('atsCvMaker.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('atsCvMaker.previewEmpty')}
                        idleHint={t('atsCvMaker.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a CV taller than the screen
                            cannot stay pinned, so the page scrolls inside it. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconBriefcase size={14} stroke={1.8} />
                                {t('atsCvMaker.livePreview')}
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col">
                                <AtsCvPreviewFrame
                                    form={form}
                                    previewRef={previewRef}
                                    template={template}
                                    zoom={pageZoom.zoom}
                                />
                            </div>
                        </div>
                    </ToolPreviewPanel>
                    </div>
                )}
            />

            {downloadModalOpen ? (
                <div
                    className="fixed inset-0 z-[120] flex items-center justify-center bg-[#1C1815]/70 p-4 backdrop-blur-[3px]"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ats-cv-download-modal-title"
                >
                    <div className="w-full max-w-md rounded-2xl border border-[#ECE8E4] bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
                        <h2
                            id="ats-cv-download-modal-title"
                            className="text-[16px] font-bold text-[#1C1815] dark:text-zinc-100"
                        >
                            {t('atsCvMaker.downloadModal.title')}
                        </h2>
                        <p className="mt-2 text-[13px] text-[#6B635C] dark:text-zinc-400">
                            {t('atsCvMaker.downloadModal.hint')}
                        </p>
                        <input
                            type="text"
                            value={downloadFileName}
                            onChange={(e) => setDownloadFileName(e.target.value)}
                            placeholder={t('atsCvMaker.downloadModal.placeholder')}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && downloadFileName.trim() && !downloading) {
                                    e.preventDefault();
                                    handleConfirmDownloadPdf();
                                }
                            }}
                            className={`${inputBaseClass} ${inputOkClass} mt-4`}
                        />
                        <p className="mt-1.5 text-[11px] text-[#A8A099] dark:text-zinc-500">
                            {t('atsCvMaker.downloadModal.extensionHint')}
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    if (!downloading) setDownloadModalOpen(false);
                                }}
                                disabled={downloading}
                                className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-[13px] font-semibold text-[#3D3833] transition-colors hover:border-orange-300 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                {t('atsCvMaker.downloadModal.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDownloadPdf}
                                disabled={!downloadFileName.trim() || downloading}
                                className="rounded-xl bg-[#1E9E5E] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#178A50] disabled:opacity-50"
                            >
                                {downloading
                                    ? t('atsCvMaker.footer.downloading')
                                    : t('atsCvMaker.downloadModal.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
